import { Link } from '@inertiajs/react'
import { Plus, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TaskStatusIcon } from '@/frontend/features/Fde/projectTaskPresentation'
import { fdeProjectPath, fdeRoutes } from '@/frontend/features/Fde/routes'
import type { ProjectTask, FdeCopy } from '@/frontend/features/Fde/types'

interface Props {
  task: ProjectTask
  projectId: string
  copy: FdeCopy
  canManage: boolean
}
export default function TaskSubtasks({ task, projectId, copy, canManage }: Props) {
  const children = task.subtasks ?? []
  if (!canManage && !children.length) return null
  if (!children.length)
    return (
      <Button asChild variant="ghost" size="sm" className="mb-3">
        <Link
          href={fdeProjectPath(`${fdeRoutes.projectTasks}/new`, projectId, {
            parent_task_id: task.id,
          })}
        >
          <Plus className="size-3.5" />
          {copy.management.add_subtask}
        </Link>
      </Button>
    )
  return (
    <details open className="group mb-6 border-t border-border/60 pt-4">
      <summary className="flex cursor-pointer list-none items-center gap-2 text-xs font-medium">
        <ChevronRight className="size-3.5 transition-transform group-open:rotate-90" />
        {copy.management.subtasks}
        {children.length > 0 && (
          <span className="font-normal tabular-nums text-muted-foreground">
            {task.subtask_progress?.completed ?? 0}/{children.length}
          </span>
        )}
      </summary>
      <div className="mt-2 divide-y divide-border/50">
        {children.map((child) => (
          <Link
            key={child.id}
            href={fdeProjectPath(`${fdeRoutes.projectTasks}/${child.id}`, projectId)}
            className="flex items-center gap-2.5 py-3 text-sm hover:text-primary"
          >
            <span title={copy.management.statuses[child.status]}>
              <TaskStatusIcon status={child.status} className="size-3.5" />
            </span>
            <span className="text-[10px] text-muted-foreground">{child.identifier}</span>
            <span className="min-w-0 flex-1">{child.title}</span>
            <span className="hidden text-xs text-muted-foreground sm:block">
              {child.assignees.join(', ')}
            </span>
          </Link>
        ))}
      </div>
      {canManage && (
        <Button asChild variant="ghost" size="sm" className="mt-1">
          <Link
            href={fdeProjectPath(`${fdeRoutes.projectTasks}/new`, projectId, {
              parent_task_id: task.id,
            })}
          >
            <Plus className="size-3.5" />
            {copy.management.add_subtask}
          </Link>
        </Button>
      )}
    </details>
  )
}
